import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './SolicitudesSection.css';
import {
  listRequests,
  createRequest,
  updateRequest,
  deleteRequest,
  setRequestStatus,
  REQUEST_SERVICE_TYPES,
  REQUEST_STATUS_OPTIONS,
  getRequestById,
} from '../../../services/requestsApi';
import {
  listRequestProposals,
  setProposalStatus,
  PROPOSAL_STATUS,
} from '../../../services/requestProposalsApi';
import { createNotification } from '../../../services/notificationsApi';
import LocationAutocompleteInput from '../../../components/LocationAutocompleteInput';
import {
  FiEdit2,
  FiTrash2,
  FiInfo,
  FiX,
  FiArrowRight,
  FiSearch,
  FiActivity,
  FiCheckCircle,
  FiLayers,
} from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
const REQUEST_CURRENCY_OPTIONS = ['MXN', 'USD'];

const emptyForm = {
  title: '',
  serviceType: 'transport',
  pickupLocation: '',
  dropoffLocation: '',
  neededBy: '',
  budget: '',
  currency: REQUEST_CURRENCY_OPTIONS[0],
  details: '',
  status: 'draft',
};

const DEFAULT_LIMIT = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const REQUEST_STATUS_FILTERS = {
  all: null,
  active: ['published', 'in_progress'],
  finished: ['completed', 'cancelled'],
};

const REQUEST_KPI_STORAGE_KEY = 'flotte:requestsKpiCollapsed';

const statusColorMap = {
  draft: 'draft',
  published: 'published',
  in_progress: 'progress',
  completed: 'completed',
  cancelled: 'cancelled',
};

const PROPOSAL_STATUS_LABELS = {
  [PROPOSAL_STATUS.PENDING]: 'Pendiente',
  [PROPOSAL_STATUS.ACCEPTED]: 'Aceptada',
  [PROPOSAL_STATUS.REJECTED]: 'Rechazada',
};

const buildServiceTypeLookup = () => {
  const map = {};
  REQUEST_SERVICE_TYPES.forEach(option => {
    map[option.value] = option.label;
  });
  return map;
};

const buildStatusLookup = () => {
  const map = {};
  REQUEST_STATUS_OPTIONS.forEach(option => {
    map[option.value] = option.label;
  });
  return map;
};

const parseBudgetValue = value => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/[^\d.,-]/g, '').replace(/,/g, '');
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
};

const formatBudgetDisplay = (value, currency = REQUEST_CURRENCY_OPTIONS[0]) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const normalizedCurrency =
    typeof currency === 'string' && currency.trim().length === 3
      ? currency.trim().toUpperCase()
      : REQUEST_CURRENCY_OPTIONS[0];
  const numeric = parseBudgetValue(value);
  if (numeric > 0) {
    const locale = normalizedCurrency === 'USD' ? 'en-US' : 'es-MX';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).format(numeric);
  }
  return String(value);
};

export default function SolicitudesSection({ user, empresaId, empresaValues }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState('list');
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [proposalViewerRequest, setProposalViewerRequest] = useState(null);
  const [requestProposals, setRequestProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [proposalError, setProposalError] = useState('');
  const [proposalActionState, setProposalActionState] = useState({ tone: '', message: '' });
  const [proposalActionLoading, setProposalActionLoading] = useState('');
  const [highlightedProposalId, setHighlightedProposalId] = useState('');
  const [acceptedRequests, setAcceptedRequests] = useState({});
  const [proposalDetailsPopover, setProposalDetailsPopover] = useState({
    open: false,
    proposal: null,
    top: 0,
    left: 0,
    caret: 70,
  });
  const totalProposals = requestProposals.length;
  const acceptedProposals = useMemo(
    () => requestProposals.filter(item => item?.status === PROPOSAL_STATUS.ACCEPTED).length,
    [requestProposals]
  );
  const rejectedProposals = useMemo(
    () => requestProposals.filter(item => item?.status === PROPOSAL_STATUS.REJECTED).length,
    [requestProposals]
  );

  const setRequestAcceptedStatus = useCallback((requestId, accepted) => {
    if (!requestId) return;
    setAcceptedRequests(prev => {
      const next = { ...prev };
      if (accepted) {
        if (next[requestId]) {
          return prev;
        }
        next[requestId] = true;
        return next;
      }
      if (!next[requestId]) {
        return prev;
      }
      delete next[requestId];
      return next;
    });
  }, []);
  const proposalsRequestRef = useRef('');
  const requestsRef = useRef([]);
  const proposalsPanelRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter] = useState('all');
  const [dateRange] = useState({ from: '', to: '' });
  const [kpiCollapsed, setKpiCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(REQUEST_KPI_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(REQUEST_KPI_STORAGE_KEY, kpiCollapsed ? 'true' : 'false');
    } catch {
      // ignore
    }
  }, [kpiCollapsed]);

  const pickFirstFilled = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined) continue;
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed) {
          return trimmed;
        }
        continue;
      }
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return String(candidate);
      }
      if (typeof candidate === 'object' && candidate !== null) {
        const possible =
          candidate.name ||
          candidate.value ||
          candidate.contactName ||
          candidate.email ||
          candidate.id;
        if (typeof possible === 'string' && possible.trim()) {
          return possible.trim();
        }
        if (typeof possible === 'number' && Number.isFinite(possible)) {
          return String(possible);
        }
      }
    }
    return '';
  };

  const getClientContactInfo = useCallback(() => {
    const contactName = pickFirstFilled(
      user?.name,
      user?.username,
      empresaValues?.contactName,
      empresaValues?.name,
      user?.email
    );
    const contactEmail = pickFirstFilled(
      user?.email,
      empresaValues?.contactEmail,
      empresaValues?.ownerEmail
    );
    const contactPhone = pickFirstFilled(
      user?.phoneNumber,
      empresaValues?.contactPhone,
      empresaValues?.phoneNumber
    );
    return {
      contactName,
      contactEmail,
      contactPhone,
    };
  }, [empresaValues, user]);

  const upsertRequestInList = useCallback(updated => {
    setRequests(prev => {
      const index = prev.findIndex(item => item.id === updated.id);
      if (index === -1) {
        return [updated, ...prev];
      }
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const applyRequestStatusLocally = useCallback((requestId, nextStatus) => {
    if (!requestId || !nextStatus) return;
    setRequests(prev =>
      prev.map(item =>
        item.id === requestId
          ? {
              ...item,
              status: nextStatus,
            }
          : item
      )
    );
    setProposalViewerRequest(prev =>
      prev?.id === requestId
        ? {
            ...prev,
            status: nextStatus,
          }
        : prev
    );
  }, []);

  const syncRequestStatus = useCallback(
    async (requestId, nextStatus) => {
      if (!requestId || !nextStatus) return;
      const currentRecord = requestsRef.current.find(item => item.id === requestId);
      if (currentRecord?.status === nextStatus) {
        return;
      }
      try {
        const updatedRequest = await setRequestStatus(requestId, nextStatus);
        if (updatedRequest) {
          upsertRequestInList(updatedRequest);
          setProposalViewerRequest(prev =>
            prev?.id === requestId ? { ...prev, ...updatedRequest } : prev
          );
          return;
        }
      } catch (statusError) {
        console.warn('No se pudo actualizar el estatus de la solicitud.', statusError);
      }
      applyRequestStatusLocally(requestId, nextStatus);
    },
    [applyRequestStatusLocally, upsertRequestInList]
  );

  const loadProposals = useCallback(
    async (requestId, requestSnapshot = null) => {
      if (!requestId) return;
      proposalsRequestRef.current = requestId;
      setLoadingProposals(true);
      setProposalError('');
      setProposalActionState({ tone: '', message: '' });
      try {
        const items = await listRequestProposals(requestId, { limit: 100 });
        if (proposalsRequestRef.current !== requestId) {
          return;
        }
        setRequestProposals(items);
        const hasAccepted = items.some(
          proposal => proposal.status === PROPOSAL_STATUS.ACCEPTED
        );
        const hasProposals = items.length > 0;
        const snapshotStatus =
          requestSnapshot?.status ||
          requestsRef.current.find(entry => entry.id === requestId)?.status ||
          '';
        if (hasAccepted) {
          if (snapshotStatus !== 'completed') {
            await syncRequestStatus(requestId, 'completed');
          }
        } else if (hasProposals) {
          if (!['in_progress', 'completed'].includes(snapshotStatus)) {
            await syncRequestStatus(requestId, 'in_progress');
          }
        }
        setRequestAcceptedStatus(requestId, hasAccepted);
      } catch (error) {
        if (proposalsRequestRef.current !== requestId) {
          return;
        }
        console.error('No se pudieron cargar las propuestas de la solicitud.', error);
        setRequestProposals([]);
        setProposalError(
          error?.message || 'No pudimos cargar las propuestas de esta solicitud.'
        );
      } finally {
        if (proposalsRequestRef.current === requestId) {
          setLoadingProposals(false);
        }
      }
    },
    [setRequestAcceptedStatus, syncRequestStatus]
  );

  const handleCloseProposals = useCallback(() => {
    setProposalViewerRequest(null);
    setRequestProposals([]);
    setLoadingProposals(false);
    setProposalError('');
    setProposalActionState({ tone: '', message: '' });
    setProposalActionLoading('');
    setHighlightedProposalId('');
    setProposalDetailsPopover({ open: false, proposal: null, top: 0, left: 0, caret: 70 });
    proposalsRequestRef.current = '';
  }, []);

  const handleShowProposalDetails = useCallback(
    (event, proposal) => {
      const triggerEl = event?.currentTarget;
      if (!proposal || !proposalsPanelRef.current || !triggerEl) return;

      setProposalDetailsPopover(prev => {
        if (prev.open && prev.proposal?.id === proposal.id) {
          return { open: false, proposal: null, top: 0, left: 0, caret: 70 };
        }

        const panelRect = proposalsPanelRef.current.getBoundingClientRect();
        const triggerRect = triggerEl.getBoundingClientRect();
        const relativeTop = triggerRect.bottom - panelRect.top + 12;
        const popoverWidth = 360;
        const horizontalCenter = triggerRect.left - panelRect.left + triggerRect.width / 2;
        const minLeft = 16;
        const maxLeft = Math.max(minLeft, panelRect.width - popoverWidth - 16);
        const desiredLeft = horizontalCenter - popoverWidth / 2;
        const safeLeft = Math.min(maxLeft, Math.max(minLeft, desiredLeft));
        const localCaret = Math.min(popoverWidth - 40, Math.max(40, horizontalCenter - safeLeft));

        return {
          open: true,
          proposal,
          top: relativeTop,
          left: safeLeft,
          caret: localCaret,
        };
      });
    },
    []
  );

  const handleCloseProposalDetailsPopover = useCallback(() => {
    setProposalDetailsPopover({ open: false, proposal: null, top: 0, left: 0, caret: 70 });
  }, []);

  const handleViewProposals = useCallback(
    request => {
      if (!request?.id) {
        return;
      }
      setHighlightedProposalId('');
      setProposalViewerRequest(request);
      setRequestProposals([]);
      setProposalError('');
      setProposalActionState({ tone: '', message: '' });
      setProposalActionLoading('');
      loadProposals(request.id, request);
    },
    [loadProposals]
  );

  const handleAcceptProposal = async proposal => {
    if (!proposal?.id || !proposalViewerRequest?.id) {
      return;
    }

    if (proposal.status === PROPOSAL_STATUS.ACCEPTED) {
      setProposalActionState({
        tone: 'info',
        message: 'Esta propuesta ya fue aceptada.',
      });
      return;
    }

    const existingAccepted = requestProposals.some(
      item => item.status === PROPOSAL_STATUS.ACCEPTED && item.id !== proposal.id
    );
    if (existingAccepted) {
      setProposalActionState({
        tone: 'info',
        message:
          'Ya tienes una propuesta aceptada para esta solicitud. Recházala antes de aceptar otra.',
      });
      return;
    }

    const { contactName, contactEmail, contactPhone } = getClientContactInfo();
    if (!contactEmail && !contactPhone) {
      setProposalActionState({
        tone: 'error',
        message:
          'Agrega un correo o teléfono de contacto en tu perfil antes de aceptar una propuesta.',
      });
      return;
    }

    const requestId = proposalViewerRequest.id;
    const requestTitle = proposalViewerRequest.title || 'tu solicitud';

    setProposalActionLoading(proposal.id);
    setProposalActionState({ tone: '', message: '' });

    try {
      const contactPayload = {
        clientContactName: contactName,
        clientContactEmail: contactEmail,
        clientContactPhone: contactPhone,
        clientContactSharedAt: new Date().toISOString(),
      };

      const accepted = await setProposalStatus(
        proposal.id,
        PROPOSAL_STATUS.ACCEPTED,
        contactPayload
      );

      if (accepted?.companyId) {
        try {
          await createNotification({
            company: accepted.companyId,
            type: 'request_proposal',
            title: `Tu propuesta para "${requestTitle}" fue aceptada`,
            message: `El cliente aceptó tu propuesta para "${requestTitle}".`,
            destinationUrl: `/dashboard?section=feed`,
            data: {
              requestId,
              proposalId: accepted.id,
              requestTitle,
              clientContactName: contactName,
              clientContactEmail: contactEmail,
              clientContactPhone: contactPhone,
            },
          });
        } catch (notifyError) {
          console.warn('No se pudo notificar al proveedor de la propuesta aceptada.', notifyError);
        }
      }

      const otherProposals = requestProposals.filter(
        item => item.id !== proposal.id && item.status !== PROPOSAL_STATUS.REJECTED
      );

      if (otherProposals.length) {
        await Promise.all(
          otherProposals.map(async item => {
            try {
              const updated = await setProposalStatus(item.id, PROPOSAL_STATUS.REJECTED);
              const rejectionCompanyId = updated?.companyId || item.companyId;
              if (rejectionCompanyId) {
                try {
                  await createNotification({
                    company: rejectionCompanyId,
                    type: 'request_proposal',
                    title: `Propuesta rechazada para "${requestTitle}"`,
                    message: 'El cliente asignó la solicitud a otro proveedor.',
                    destinationUrl: `/dashboard?section=feed`,
                    data: {
                      requestId,
                      proposalId: item.id,
                    },
                  });
                } catch (notifyError) {
                  console.warn(
                    'No se pudo notificar al proveedor de la propuesta rechazada.',
                    notifyError
                  );
                }
              }
            } catch (updateError) {
              console.warn('No se pudo actualizar la propuesta rechazada.', updateError);
            }
          })
        );
      }

      await syncRequestStatus(requestId, 'completed');

      await loadProposals(requestId, { status: 'completed' });

      setProposalActionState({
        tone: 'success',
        message: 'Propuesta aceptada. Compartimos tus datos de contacto con el proveedor.',
      });
      setRequestAcceptedStatus(requestId, true);
    } catch (error) {
      console.error('No se pudo aceptar la propuesta.', error);
      setProposalActionState({
        tone: 'error',
        message: error?.message || 'No pudimos aceptar la propuesta. Intenta nuevamente.',
      });
    } finally {
      setProposalActionLoading('');
    }
  };

  const handleRejectProposal = async proposal => {
    if (!proposal?.id || !proposalViewerRequest?.id) {
      return;
    }

    if (proposal.status === PROPOSAL_STATUS.REJECTED) {
      setProposalActionState({
        tone: 'info',
        message: 'Esta propuesta ya se encuentra rechazada.',
      });
      return;
    }

    const requestId = proposalViewerRequest.id;
    const requestTitle = proposalViewerRequest.title || 'tu solicitud';
    setProposalActionLoading(proposal.id);
    setProposalActionState({ tone: '', message: '' });

    try {
      const updated = await setProposalStatus(proposal.id, PROPOSAL_STATUS.REJECTED);
      const rejectionCompanyId = updated?.companyId || proposal.companyId;
      if (rejectionCompanyId) {
        try {
          await createNotification({
            company: rejectionCompanyId,
            type: 'request_proposal',
            title: `Propuesta rechazada para "${requestTitle}"`,
            message: 'El cliente no está interesado en la propuesta.',
            destinationUrl: `/dashboard?section=feed`,
            data: {
              requestId,
              proposalId: proposal.id,
            },
          });
        } catch (notifyError) {
          console.warn('No se pudo notificar al proveedor de la propuesta rechazada.', notifyError);
        }
      }

      await loadProposals(requestId, proposalViewerRequest);

      setProposalActionState({
        tone: 'success',
        message: 'Propuesta rechazada. Notificamos al proveedor.',
      });
    } catch (error) {
      console.error('No se pudo rechazar la propuesta.', error);
      setProposalActionState({
        tone: 'error',
        message: error?.message || 'No pudimos rechazar la propuesta. Intenta nuevamente.',
      });
    } finally {
      setProposalActionLoading('');
    }
  };

  useEffect(() => {
    if (!proposalViewerRequest) return;
    const handleKey = event => {
      if (event.key === 'Escape') {
        handleCloseProposals();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [proposalViewerRequest, handleCloseProposals]);

  const serviceTypeLabels = useMemo(buildServiceTypeLookup, []);
  const statusLabels = useMemo(buildStatusLookup, []);
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
    []
  );
  const formatDate = useCallback(
    value => {
      if (!value) {
        return 'Fecha por definir';
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return 'Fecha por definir';
      }
      return dateFormatter.format(parsed);
    },
    [dateFormatter]
  );
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0,
      }),
    []
  );
  const hasAcceptedProposal = useMemo(
    () => requestProposals.some(item => item.status === PROPOSAL_STATUS.ACCEPTED),
    [requestProposals]
  );

  const companyCategory = empresaValues?.category || '';
  const role = user?.role || '';
  const canManageRequests =
    Boolean(user?.id && empresaId) &&
    (role === 'admin' ||
      role === 'client' ||
      !companyCategory ||
      companyCategory === 'client');
  const numberFormatter = useMemo(() => new Intl.NumberFormat('es-MX'), []);

  const requestStats = useMemo(() => {
    const list = Array.isArray(requests) ? requests : [];
    if (!list.length) {
      return {
        total: 0,
        draft: 0,
        published: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
        totalBudget: 0,
        averageBudget: 0,
        lastUpdated: '',
      };
    }

    let draft = 0;
    let published = 0;
    let inProgress = 0;
    let completed = 0;
    let cancelled = 0;
    let totalBudget = 0;
    let latest = null;

    list.forEach(item => {
      switch (item.status) {
        case 'published':
          published += 1;
          break;
        case 'in_progress':
          inProgress += 1;
          break;
        case 'completed':
          completed += 1;
          break;
        case 'cancelled':
          cancelled += 1;
          break;
        default:
          draft += 1;
      }

      totalBudget += parseBudgetValue(item.budget);

      const updatedAt = item.updated || item.created;
      if (updatedAt) {
        const parsed = new Date(updatedAt);
        if (!Number.isNaN(parsed.getTime())) {
          if (!latest || parsed > latest) {
            latest = parsed;
          }
        }
      }
    });

    const averageBudget = list.length ? Math.round(totalBudget / list.length) : 0;
    const lastUpdated =
      latest && !Number.isNaN(latest.getTime()) ? dateFormatter.format(latest) : '';

    return {
      total: list.length,
      draft,
      published,
      inProgress,
      completed,
      cancelled,
      totalBudget,
      averageBudget,
      lastUpdated,
    };
  }, [dateFormatter, requests]);

  const requestKpis = useMemo(() => {
    const activeCount = (requestStats.published || 0) + (requestStats.inProgress || 0);
    const finishedCount = (requestStats.completed || 0) + (requestStats.cancelled || 0);
    const totalCount = requestStats.total || 0;
    const formatValue = value => numberFormatter.format(Math.max(0, value || 0));
    return [
      {
        id: 'active',
        label: 'Requerimientos Activos',
        value: formatValue(activeCount),
        context: 'Publicados o en progreso',
        icon: FiActivity,
        variant: activeCount ? 'success' : 'muted',
      },
      {
        id: 'finished',
        label: 'Finalizados',
        value: formatValue(finishedCount),
        context: 'Completados o cancelados',
        icon: FiCheckCircle,
        variant: finishedCount ? 'info' : 'muted',
      },
      {
        id: 'total',
        label: 'Total historico',
        value: formatValue(totalCount),
        context: 'Registrados en la plataforma',
        icon: FiLayers,
        variant: totalCount ? 'default' : 'muted',
      },
    ];
  }, [numberFormatter, requestStats.cancelled, requestStats.completed, requestStats.inProgress, requestStats.published, requestStats.total]);

  const filteredRequests = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const statuses = REQUEST_STATUS_FILTERS[statusFilter] || null;
    const fromDate = dateRange.from ? new Date(dateRange.from) : null;
    const toDate = dateRange.to ? new Date(dateRange.to) : null;

    return requests.filter(request => {
      if (statuses && statuses.length && !statuses.includes(request.status)) {
        return false;
      }

      if (term) {
        const haystack = [
          request.title,
          request.pickupLocation,
          request.dropoffLocation,
          request.details,
          serviceTypeLabels[request.serviceType],
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) {
          return false;
        }
      }

      if (fromDate || toDate) {
        const referenceValue = request.neededBy || request.updated || request.created;
        if (!referenceValue) {
          return false;
        }
        const referenceDate = new Date(referenceValue);
        if (Number.isNaN(referenceDate.getTime())) {
          return false;
        }
        if (fromDate && referenceDate < fromDate) {
          return false;
        }
        if (toDate) {
          const inclusiveTo = new Date(dateRange.to);
          inclusiveTo.setHours(23, 59, 59, 999);
          if (referenceDate > inclusiveTo) {
            return false;
          }
        }
      }

      return true;
    });
  }, [requests, searchTerm, statusFilter, dateRange.from, dateRange.to, serviceTypeLabels]);

  const sortedRequests = useMemo(() => {
    const next = [...filteredRequests];
    const getTimestamp = request => {
      const value = request.updated || request.created || request.neededBy;
      if (!value) return 0;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };
    next.sort((a, b) => getTimestamp(b) - getTimestamp(a));
    return next;
  }, [filteredRequests]);

  const totalPages = Math.max(1, Math.ceil(sortedRequests.length / pageSize) || 1);

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(0);
  }, [pageSize, searchTerm, statusFilter, dateRange.from, dateRange.to]);

  const paginatedRequests = useMemo(() => {
    if (loading) return [];
    const start = page * pageSize;
    return sortedRequests.slice(start, start + pageSize);
  }, [loading, page, pageSize, sortedRequests]);

  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index),
    [totalPages]
  );


  useEffect(() => {
    let ignore = false;

    const load = async () => {
      if (!user?.id || !empresaId) {
        if (!ignore) {
          setRequests([]);
        }
        return;
      }

      setLoading(true);
      setError('');
      try {
        const items = await listRequests({
          companyId: empresaId,
          createdBy: user.id,
          limit: DEFAULT_LIMIT,
        });
        if (!ignore) {
          setRequests(items);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(
            loadError?.message || 'No pudimos cargar tus requerimientos por ahora.'
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [user?.id, empresaId]);

  useEffect(() => {
    setAcceptedRequests(prev => {
      const next = { ...prev };
      let changed = false;
      const existingIds = new Set(requests.map(item => item.id));

      Object.keys(next).forEach(id => {
        if (!existingIds.has(id)) {
          delete next[id];
          changed = true;
        }
      });

      requests.forEach(item => {
        const shouldBeAccepted =
          item.status === 'in_progress' || item.status === 'completed';
        if (shouldBeAccepted) {
          if (!next[item.id]) {
            next[item.id] = true;
            changed = true;
          }
        } else if (next[item.id]) {
          delete next[item.id];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [requests]);

  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setMode('list');
    setError('');
    setSuccess('');
  };

  const handleCreate = useCallback(() => {
    setForm({ ...emptyForm, status: 'draft' });
    setEditingId(null);
    setMode('form');
    setError('');
    setSuccess('');
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const intent = params.get('intent');
    if (intent !== 'create_request') return;
    handleCreate();
    params.delete('intent');
    if (!params.get('section')) {
      params.set('section', 'solicitudes');
    }
    const nextSearch = params.toString();
    navigate(`/dashboard?${nextSearch}`, { replace: true });
  }, [location.search, handleCreate, navigate]);

  useEffect(() => {
    if (!highlightedProposalId) return;
    if (!proposalViewerRequest) return;
    if (!requestProposals.length) return;
    const exists = requestProposals.some(item => item.id === highlightedProposalId);
    if (!exists) return;
    setProposalActionState(prev =>
      prev?.message
        ? prev
        : {
            tone: 'info',
            message: 'Abrimos la propuesta que acabas de recibir.',
          }
    );
    const element = document.getElementById(`proposal-${highlightedProposalId}`);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 180);
    }
  }, [highlightedProposalId, requestProposals, proposalViewerRequest]);

  const handleEdit = request => {
    if (acceptedRequests[request?.id]) {
      setError('No puedes editar una solicitud en progreso o con propuesta aceptada.');
      return;
    }
    setForm({
      title: request.title || '',
      serviceType: request.serviceType || 'transport',
      pickupLocation: request.pickupLocation || '',
      dropoffLocation: request.dropoffLocation || '',
      neededBy: request.neededBy || '',
      budget: request.budget || '',
      currency: request.currency || REQUEST_CURRENCY_OPTIONS[0],
      details: request.details || '',
      status: request.status || 'draft',
    });
    setEditingId(request.id);
    setMode('form');
    setError('');
    setSuccess('');
  };

  const handleFormChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const intent = params.get('intent');
    if (intent !== 'view_request_proposals') return;
    const requestId = params.get('request');
    if (!requestId) return;
    if (loading) return;

    const proposalIdParam = params.get('proposal') || '';

    const openFromNotification = async () => {
      let requestRecord = requests.find(entry => entry.id === requestId);
      if (!requestRecord) {
        try {
          requestRecord = await getRequestById(requestId);
          if (requestRecord) {
            upsertRequestInList(requestRecord);
          }
        } catch (error) {
          console.warn('No se pudo obtener la solicitud vinculada a la notificación.', error);
          setProposalError('No pudimos cargar la solicitud vinculada a la notificación.');
          return;
        }
      }
      if (requestRecord) {
        setHighlightedProposalId(proposalIdParam);
        handleViewProposals(requestRecord);
      }
    };

    openFromNotification();

    const nextParams = new URLSearchParams(location.search);
    nextParams.delete('intent');
    nextParams.delete('proposal');
    const nextSearch = nextParams.toString();
    const nextHref = nextSearch
      ? `/dashboard?${nextSearch}`
      : '/dashboard?section=solicitudes';
    navigate(nextHref, { replace: true });
  }, [
    location.search,
    loading,
    requests,
    handleViewProposals,
    navigate,
    upsertRequestInList,
  ]);

  const removeRequestFromList = id => {
    setRequests(prev => prev.filter(item => item.id !== id));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!canManageRequests) {
      setError('Tu cuenta no puede crear requerimientos.');
      return;
    }

    const payload = {
      ...form,
      company: empresaId,
      createdBy: user?.id,
    };

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let saved;
      if (editingId) {
        saved = await updateRequest(editingId, payload);
      } else {
        saved = await createRequest(payload);
      }
      upsertRequestInList(saved);
      setSuccess(editingId ? 'Requerimiento actualizado.' : 'Requerimiento creado.');
      setMode('list');
      setForm({ ...emptyForm });
      setEditingId(null);
    } catch (submitError) {
      setError(submitError?.message || 'No pudimos guardar el requerimiento.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async request => {
    if (!canManageRequests || !request?.id) return;
    if (acceptedRequests[request.id]) {
      setError('No puedes eliminar una solicitud en progreso o con propuesta aceptada.');
      return;
    }
    const confirmDelete = window.confirm(
      `Eliminar el requerimiento "${request.title}"?`
    );
    if (!confirmDelete) return;

    try {
      await deleteRequest(request.id);
      removeRequestFromList(request.id);
      setSuccess('Requerimiento eliminado.');
    } catch (deleteError) {
      setError(deleteError?.message || 'No pudimos eliminar el requerimiento.');
    }
  };

  const handlePageSizeChange = useCallback(event => {
    setPageSize(Number(event.target.value));
  }, []);

  const renderList = () => (
    <div className="solicitudes-shell solicitudes-shell--dashboard">
      <div className="solicitudes-layout">
        <div className={`dashboard-kpi-wrapper${kpiCollapsed ? ' is-collapsed' : ''}`}>
          <div className="dashboard-kpi-wrapper__controls">
            <span className="dashboard-kpi-wrapper__label">Indicadores</span>
            <button
              type="button"
              className="dashboard-kpi-toggle"
              onClick={() => setKpiCollapsed(prev => !prev)}
              aria-pressed={!kpiCollapsed}
            >
              {kpiCollapsed ? 'Mostrar' : 'Ocultar'}
            </button>
          </div>
          {!kpiCollapsed ? (
            <section className="solicitudes-kpi-grid" aria-label="Indicadores de requerimientos">
              {requestKpis.map(card => {
                const Icon = card.icon;
                return (
                  <article key={card.id} className="solicitudes-kpi-card">
                    <span
                      className={`solicitudes-kpi-card__icon solicitudes-kpi-card__icon--${card.variant}`}
                      aria-hidden="true"
                    >
                      <Icon />
                    </span>
                    <div className="solicitudes-kpi-card__body">
                      <p className="solicitudes-kpi-card__label">{card.label}</p>
                      <strong
                        className={`solicitudes-kpi-card__value solicitudes-kpi-card__value--${card.variant}`}
                      >
                        {card.value}
                      </strong>
                      <span className="solicitudes-kpi-card__context">{card.context}</span>
                    </div>
                  </article>
                );
              })}
            </section>
          ) : null}
        </div>

        <div className="solicitudes-table-card">
          <div className="solicitudes-toolbar">
            <div className="solicitudes-toolbar__context">
              <div className="solicitudes-toolbar__heading">
                <h2 className="solicitudes-toolbar__title">Requerimientos de servicio</h2>
              </div>
              <label className="solicitudes-search" htmlFor="solicitudes-search-input">
                <FiSearch aria-hidden="true" />
                <input
                  id="solicitudes-search-input"
                  type="search"
                  placeholder="Buscar requerimiento..."
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                />
              </label>
            </div>
            <div className="solicitudes-toolbar__actions">
              <button
                type="button"
                className="dashboard-action-btn"
                onClick={handleCreate}
                disabled={!canManageRequests}
              >
                Nuevo requerimiento
              </button>
            </div>
          </div>
          <div className="solicitudes-table-card__body">

        {error ? <div className="solicitudes-error">{error}</div> : null}
        {success ? <div className="solicitudes-success">{success}</div> : null}
        {!canManageRequests ? (
          <div className="solicitudes-info">
            <strong>Solo empresas clientes pueden crear requerimientos.</strong>
            <span>
              Actualiza la categoria de tu empresa a &quot;client&quot; o contacta a soporte para
              habilitar esta seccion.
            </span>
          </div>
        ) : null}

        <div className="table-scroll-hint" role="note">
          <span aria-hidden="true" className="table-scroll-hint__icon" />
          <span className="table-scroll-hint__text">
            Desliza horizontalmente, haz clic en un encabezado para ordenar (A-Z/Z-A), clic derecho para
            configurar columnas o arrastra el borde para ajustar el ancho.
          </span>
        </div>
        <div className="solicitudes-table-wrapper">
          <table className="solicitudes-table">
            <thead>
              <tr>
                <th scope="col">Requerimiento</th>
                <th scope="col">Necesito para</th>
                <th scope="col">Presupuesto</th>
                <th scope="col">Estado</th>
                <th scope="col" className="solicitudes-table-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: Math.min(pageSize, 3) }).map((_, index) => (
                    <tr key={`solicitud-placeholder-${index}`} className="solicitudes-table-placeholder">
                      <td colSpan={5} aria-hidden="true">
                        &nbsp;
                      </td>
                    </tr>
                  ))
                : paginatedRequests.length
                ? paginatedRequests.map(item => {
                    const statusClass = statusColorMap[item.status] || 'draft';
                    const budgetLabel =
                      formatBudgetDisplay(item.budget, item.currency) || 'Pendiente';
                    const updatedDate = item.updated || item.created;
                    const updatedLabel = updatedDate
                      ? dateFormatter.format(new Date(updatedDate))
                      : 'Sin registro';
                    const createdLabel = item.created
                      ? dateFormatter.format(new Date(item.created))
                      : 'Sin registro';
                    const neededByLabel = item.neededBy ? formatDate(item.neededBy) : 'Fecha por definir';
                    const routeFrom = item.pickupLocation || 'Origen por definir';
                    const routeTo = item.dropoffLocation || 'Destino por definir';
                    const statusLabel = statusLabels[item.status] || item.status || 'Sin estado';
                    const manageActionsLocked = ['in_progress', 'completed'].includes(item.status);
                    const canEditRequest = canManageRequests && !manageActionsLocked;
                    const primaryLabel = canEditRequest ? 'Gestionar' : 'Ver respuestas';
                    const handlePrimaryAction = () => {
                      if (canEditRequest) {
                        handleEdit(item);
                        return;
                      }
                      handleViewProposals(item);
                    };
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="solicitudes-table-main">
                            <div>
                              <p className="solicitudes-table-title">
                                {item.title || 'Requerimiento sin titulo'}
                              </p>
                              <span className="solicitudes-table-note">
                                {serviceTypeLabels[item.serviceType] || 'Servicio sin clasificar'}
                              </span>
                              <span className="solicitudes-table-route">
                                {routeFrom}
                                <FiArrowRight aria-hidden="true" />
                                {routeTo}
                              </span>
                              <span className="solicitudes-table-note">Actualizada: {updatedLabel}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong>{neededByLabel}</strong>
                          <span className="solicitudes-table-note">Creada: {createdLabel}</span>
                        </td>
                        <td>{budgetLabel}</td>
                        <td>
                          <span className={`solicitud-badge solicitud-badge--${statusClass}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="solicitudes-table-col-actions">
                          <div className="solicitudes-table-actions">
                            <button
                              type="button"
                              className="solicitudes-table-action solicitudes-table-action--primary"
                              onClick={handlePrimaryAction}
                            >
                              {primaryLabel}
                            </button>
                            <button
                              type="button"
                              className="solicitudes-table-action solicitudes-table-action--ghost"
                              onClick={() => handleViewProposals(item)}
                            >
                              Respuestas
                            </button>
                            {canEditRequest ? (
                              <div className="solicitudes-table-action-icons">
                                <button
                                  type="button"
                                  onClick={() => handleEdit(item)}
                                  aria-label={`Editar ${item.title || 'requerimiento'}`}
                                >
                                  <FiEdit2 aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item)}
                                  aria-label={`Eliminar ${item.title || 'requerimiento'}`}
                                >
                                  <FiTrash2 aria-hidden="true" />
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                : (
                    <tr className="solicitudes-table-empty">
                      <td colSpan={5}>
                        <strong>No encontramos requerimientos con estos filtros.</strong>
                        <span>Ajusta la búsqueda para ver otros resultados.</span>
                      </td>
                    </tr>
                  )}
            </tbody>
          </table>
        </div>
        </div>

        <div className="solicitudes-table-card__footer">
          <div className="table-pagination">
            <div className="table-pagination__page-size">
              <label>
                Mostrar
                <select value={pageSize} onChange={handlePageSizeChange}>
                  {PAGE_SIZE_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                requerimientos
              </label>
            </div>
            <div className="table-pagination__controls" role="navigation" aria-label="Paginacion de requerimientos">
              <button type="button" onClick={() => setPage(prev => Math.max(prev - 1, 0))} disabled={page === 0}>
                Anterior
              </button>
              <div className="table-pagination__pages">
                {pageNumbers.map(number => (
                  <button
                    type="button"
                    key={`request-page-${number}`}
                    className={number === page ? 'active' : ''}
                    aria-current={number === page ? 'page' : undefined}
                    onClick={() => setPage(number)}
                  >
                    {number + 1}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages - 1))}
                disabled={page >= totalPages - 1}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
      {proposalViewerRequest ? (
        <div
          className="solicitudes-proposals-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="solicitudes-proposals-title"
        >
          <div className="solicitudes-proposals-panel" ref={proposalsPanelRef}>
            <header className="solicitudes-proposals-panel__header">
              <div className="solicitudes-proposals-panel__heading">
                <h3 id="solicitudes-proposals-title">Propuestas recibidas</h3>
                <p>
                  {proposalViewerRequest.title || 'Solicitud sin titulo'}
                  {totalProposals
                    ? ` · ${totalProposals} ${
                        totalProposals === 1 ? 'propuesta' : 'propuestas'
                      }`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                className="solicitudes-proposals-close"
                onClick={handleCloseProposals}
              >
                Cerrar
              </button>
            </header>
            <div className="solicitudes-proposals-panel__body">
              {!loadingProposals && !proposalError && totalProposals > 0 ? (
                <>
                  <div className="solicitudes-proposals-panel__stats">
                    <div>
                      <p>Total propuestas</p>
                      <strong>{totalProposals}</strong>
                    </div>
                    <div>
                      <p>Aceptadas</p>
                      <strong>{acceptedProposals}</strong>
                    </div>
                    <div>
                      <p>Rechazadas</p>
                      <strong>{rejectedProposals}</strong>
                    </div>
                  </div>
                  <p className="solicitudes-proposals-panel__notice solicitudes-proposals-hint solicitudes-proposals-hint--info">
                    Solo puedes aceptar una propuesta a la vez. Si aceptas otra, la anterior se marcará automáticamente como rechazada.
                  </p>
                </>
              ) : null}
              {proposalActionState.message ? (
                <div
                  className={`solicitudes-proposals-alert solicitudes-proposals-alert--${proposalActionState.tone ||
                    'info'}`}
                >
                  {proposalActionState.message}
                </div>
              ) : null}
              {proposalError ? (
                <div className="solicitudes-proposals-error">{proposalError}</div>
              ) : null}
              {loadingProposals ? (
                <div className="solicitudes-proposals-loading">Cargando propuestas…</div>
              ) : null}
              {!loadingProposals && !proposalError && totalProposals === 0 ? (
                <div className="solicitudes-proposals-empty">
                  <strong>No hay propuestas registradas.</strong>
                  <span>Cuando un proveedor envíe su propuesta aparecerá en esta lista.</span>
                </div>
              ) : null}
              {!loadingProposals && !proposalError && totalProposals > 0 ? (
                <div className="solicitudes-proposals-table-wrapper">
                  <table className="solicitudes-proposals-table">
                    <thead>
                      <tr>
                        <th scope="col">Proveedor</th>
                        <th scope="col">Enviada</th>
                        <th scope="col">Monto</th>
                        <th scope="col">Estatus</th>
                        <th scope="col">Comentarios y contacto</th>
                        <th scope="col" className="solicitudes-proposals-col-actions">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                {requestProposals.map(proposal => {
                  const accepted = proposal.status === PROPOSAL_STATUS.ACCEPTED;
                  const pending = proposal.status === PROPOSAL_STATUS.PENDING;
                  const statusLabel = PROPOSAL_STATUS_LABELS[proposal.status] || proposal.status;
                  const companyName = proposal.company?.name || 'Proveedor sin identificar';
                  const createdLabel = proposal.created
                    ? dateFormatter.format(new Date(proposal.created))
                    : '';
                  const assetReference = proposal.assetReference || '';
                  const isHighlighted = highlightedProposalId === proposal.id;
                  const rowClasses = [
                    'solicitudes-proposals-row',
                    `solicitudes-proposals-row--${proposal.status}`,
                  ];
                  if (isHighlighted) {
                    rowClasses.push('solicitudes-proposals-row--highlighted');
                  }
                  const priceLabel = (() => {
                    const { price } = proposal;
                    if (price === null || price === undefined) {
                      return '';
                    }
                    if (typeof price === 'number' && Number.isFinite(price)) {
                      return currencyFormatter.format(price);
                    }
                    if (typeof price === 'string') {
                      const normalized = price.replace(/[^\d.,-]/g, '').replace(/,/g, '');
                      const numeric = Number(normalized);
                      if (Number.isFinite(numeric)) {
                        return currencyFormatter.format(numeric);
                      }
                      const trimmed = price.trim();
                      return trimmed;
                    }
                    return String(price);
                  })();

                  return (
                    <tr
                      key={proposal.id}
                      id={`proposal-${proposal.id}`}
                      data-proposal-id={proposal.id}
                      className={rowClasses.join(' ')}
                    >
                      <td
                        data-label="Proveedor"
                        className="solicitudes-proposals-cell solicitudes-proposals-cell--company"
                      >
                        <strong>{companyName}</strong>
                        {proposal.providerName && !accepted ? (
                          <span className="solicitudes-proposals-provider">
                            Contacto: {proposal.providerName}
                          </span>
                        ) : null}
                        {assetReference ? (
                          <span className="solicitudes-proposals-reference">
                            Referencia: {assetReference}
                          </span>
                        ) : null}
                      </td>
                      <td
                        data-label="Enviada"
                        className="solicitudes-proposals-cell solicitudes-proposals-cell--date"
                      >
                        {createdLabel ? <span>{createdLabel}</span> : <span>Sin fecha</span>}
                      </td>
                      <td
                        data-label="Monto"
                        className="solicitudes-proposals-cell solicitudes-proposals-cell--price"
                      >
                        {priceLabel ? (
                          <span className="solicitudes-proposals-price">{priceLabel}</span>
                        ) : (
                          <span className="solicitudes-proposals-price solicitudes-proposals-price--placeholder">
                            Pendiente
                          </span>
                        )}
                      </td>
                      <td
                        data-label="Estatus"
                        className="solicitudes-proposals-cell solicitudes-proposals-cell--status"
                      >
                        <span
                          className={`solicitudes-proposals-status solicitudes-proposals-status--${proposal.status}`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td
                        data-label="Comentarios y contacto"
                        className="solicitudes-proposals-cell solicitudes-proposals-cell--notes"
                      >
                        <button
                          type="button"
                          className={`solicitudes-proposals-details-toggle${
                            proposalDetailsPopover.open &&
                            proposalDetailsPopover.proposal?.id === proposal.id
                              ? ' is-active'
                              : ''
                          }`}
                          onClick={event => handleShowProposalDetails(event, proposal)}
                          aria-haspopup="true"
                          aria-expanded={
                            proposalDetailsPopover.open &&
                            proposalDetailsPopover.proposal?.id === proposal.id
                          }
                        >
                          <FiInfo aria-hidden="true" />
                          <span>Ver detalles</span>
                        </button>
                      </td>
                      <td
                        data-label="Acciones"
                        className="solicitudes-proposals-cell solicitudes-proposals-cell--actions"
                      >
                        {pending ? (
                          <div className="solicitudes-proposals-actions">
                            <button
                              type="button"
                              className="solicitudes-proposals-btn solicitudes-proposals-btn--primary"
                              onClick={() => handleAcceptProposal(proposal)}
                              disabled={
                                proposalActionLoading === proposal.id || hasAcceptedProposal
                              }
                            >
                              {proposalActionLoading === proposal.id
                                ? 'Procesando...'
                                : 'Aceptar propuesta'}
                            </button>
                            <button
                              type="button"
                              className="solicitudes-proposals-btn solicitudes-proposals-btn--secondary"
                              onClick={() => handleRejectProposal(proposal)}
                              disabled={proposalActionLoading === proposal.id}
                            >
                              {proposalActionLoading === proposal.id ? 'Procesando...' : 'Rechazar'}
                            </button>
                          </div>
                        ) : (
                          <span className="solicitudes-proposals-hint solicitudes-proposals-hint--muted">
                            {accepted ? 'Propuesta aceptada' : 'Sin acciones disponibles'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {proposalDetailsPopover.open && proposalDetailsPopover.proposal ? (
                <div
                  className="solicitudes-proposals-popover"
                  role="dialog"
                  aria-live="polite"
                  style={{
                    top: proposalDetailsPopover.top,
                    left: proposalDetailsPopover.left,
                    '--popover-caret-offset': `${proposalDetailsPopover.caret}px`,
                  }}
                >
                  <div className="solicitudes-proposals-popover__header">
                    <div>
                      <p>Proveedor</p>
                      <strong>
                        {proposalDetailsPopover.proposal.company?.name ||
                          proposalDetailsPopover.proposal.providerName ||
                          'Proveedor sin identificar'}
                      </strong>
                    </div>
                    <button
                      type="button"
                      className="solicitudes-proposals-popover__close"
                      onClick={handleCloseProposalDetailsPopover}
                      aria-label="Cerrar detalles"
                    >
                      <FiX aria-hidden="true" />
                    </button>
                  </div>
                  <div className="solicitudes-proposals-popover__body">
                    {proposalDetailsPopover.proposal.details ? (
                      <p className="solicitudes-proposals-note">
                        {proposalDetailsPopover.proposal.details}
                      </p>
                    ) : (
                      <p className="solicitudes-proposals-note is-empty">Sin comentarios adicionales.</p>
                    )}
                    {proposalDetailsPopover.proposal.status === PROPOSAL_STATUS.ACCEPTED ? (
                      <div className="solicitudes-proposals-contact">
                        <h4>Datos de contacto del proveedor</h4>
                        {proposalDetailsPopover.proposal.providerName ? (
                          <span>{proposalDetailsPopover.proposal.providerName}</span>
                        ) : null}
                        {proposalDetailsPopover.proposal.providerEmail ? (
                          <a href={`mailto:${proposalDetailsPopover.proposal.providerEmail}`}>
                            {proposalDetailsPopover.proposal.providerEmail}
                          </a>
                        ) : null}
                        {proposalDetailsPopover.proposal.providerPhone ? (
                          <a
                            href={`tel:${
                              typeof proposalDetailsPopover.proposal.providerPhone === 'string'
                                ? proposalDetailsPopover.proposal.providerPhone.replace(/\s+/g, '')
                                : proposalDetailsPopover.proposal.providerPhone
                            }`}
                          >
                            {proposalDetailsPopover.proposal.providerPhone}
                          </a>
                        ) : null}
                        {!proposalDetailsPopover.proposal.providerEmail &&
                        !proposalDetailsPopover.proposal.providerPhone ? (
                          <span>El proveedor no incluyó medios de contacto adicionales.</span>
                        ) : null}
                      </div>
                    ) : (
                      <p className="solicitudes-proposals-hint solicitudes-proposals-hint--info">
                        Acepta la propuesta para revelar los datos de contacto del proveedor.
                      </p>
                    )}
                    {proposalDetailsPopover.proposal.status === PROPOSAL_STATUS.PENDING &&
                    hasAcceptedProposal ? (
                      <p className="solicitudes-proposals-hint solicitudes-proposals-hint--alert">
                        Ya tienes una propuesta aceptada. Recházala para seleccionar otra.
                      </p>
                    ) : null}
                    {proposalDetailsPopover.proposal.status === PROPOSAL_STATUS.REJECTED ? (
                      <p className="solicitudes-proposals-hint solicitudes-proposals-hint--muted">
                        Esta propuesta fue rechazada.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderForm = () => (
    <div className="solicitudes-form-layout">
      <div className="solicitudes-form-toolbar">
        <div className="solicitudes-form-toolbar-left">
          <button type="button" className="btn btn-secondary" onClick={resetForm}>
            Cancelar
          </button>
        </div>
        <div className="solicitudes-form-toolbar-actions">
          <button type="submit" form="solicitud-form" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Guardar requerimiento'}
          </button>
        </div>
      </div>

      <section className="solicitudes-form-shell">
        <header className="solicitudes-form-header">
          <div>
            <h2>{editingId ? 'Editar requerimiento' : 'Nuevo requerimiento'}</h2>
            <p>
              Describe el servicio que necesitas. Publicaremos tu requerimiento para proveedores
              verificados cuando cambies el estatus a &quot;Publicado&quot;.
            </p>
          </div>
        </header>

        {error && <div className="solicitudes-error">{error}</div>}

        <form id="solicitud-form" onSubmit={handleSubmit} className="solicitudes-form">
          <div className="solicitudes-form-grid">
            <section className="solicitudes-card">
              <header className="solicitudes-card__head">
                <div>
                  <h3>Datos del servicio</h3>
                  <p>Indica el tipo de asistencia, origen y destino del requerimiento.</p>
                </div>
              </header>
              <div className="solicitudes-field-grid">
                <label className="solicitudes-field solicitudes-field--full">
                  <span>Titulo</span>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleFormChange}
                    required
                    maxLength={160}
                    placeholder="Transporte urgente CDMX - Monterrey"
                  />
                </label>
                <label className="solicitudes-field">
                  <span>Tipo de servicio</span>
                  <select
                    name="serviceType"
                    value={form.serviceType}
                    onChange={handleFormChange}
                    required
                  >
                    {REQUEST_SERVICE_TYPES.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="solicitudes-field">
                  <span>Fecha requerida</span>
                  <input
                    type="date"
                    name="neededBy"
                    value={form.neededBy}
                    onChange={handleFormChange}
                  />
                </label>
                <label className="solicitudes-field">
                  <span>Origen</span>
                  <LocationAutocompleteInput
                    name="pickupLocation"
                    value={form.pickupLocation}
                    onChange={handleFormChange}
                    placeholder="Ciudad de Mexico, CDMX"
                  />
                </label>
                <label className="solicitudes-field">
                  <span>Destino</span>
                  <LocationAutocompleteInput
                    name="dropoffLocation"
                    value={form.dropoffLocation}
                    onChange={handleFormChange}
                    placeholder="Monterrey, NL"
                  />
                </label>
              </div>
            </section>

            <section className="solicitudes-card">
              <header className="solicitudes-card__head">
                <div>
                  <h3>Detalles y publicacion</h3>
                  <p>Agrega contexto adicional para tu requerimiento.</p>
                </div>
              </header>
              <div className="solicitudes-field-grid">
                <label className="solicitudes-field">
                  <span>Presupuesto (opcional)</span>
                  <input
                    type="text"
                    name="budget"
                    value={form.budget}
                    onChange={handleFormChange}
                    placeholder="Ej. $45,000 MXN"
                    maxLength={120}
                  />
                </label>
                <label className="solicitudes-field">
                  <span>Divisa</span>
                  <select
                    name="currency"
                    value={form.currency}
                    onChange={handleFormChange}
                  >
                    {REQUEST_CURRENCY_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="solicitudes-field solicitudes-field--full">
                  <span>Detalles adicionales</span>
                  <textarea
                    name="details"
                    value={form.details}
                    onChange={handleFormChange}
                    rows={6}
                    placeholder="Describe horarios, tipo de carga, requisitos de seguridad o cualquier informacion relevante."
                    maxLength={2000}
                  />
                </label>
              </div>
            </section>
          </div>
        </form>
      </section>
    </div>
  );

  return mode === 'form' ? renderForm() : renderList();
}
